import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Button,
  message,
  Spin,
  Input,
  Tag,
  Typography,
  Switch,
} from "antd";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  ReloadOutlined,
  FileAddOutlined,
  InboxOutlined,
  FileSearchOutlined,
  PlusOutlined,
  CalendarOutlined,
  BarcodeOutlined,
} from "@ant-design/icons";
import {
  HEADERS_EXPORT_BEN,
  POST_ACCEPT_RECHECK_JUDGE_DOC,
  POST_BILL_PAYMENT,
  POST_LOAD_DETAIL_OLD_DATA,
  POST_LOAN_DB2,
  POST_MAIN_RECHECK_JUDGE_DOC,
  POST_ROUND_AGREEMENT,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import ReCheckJudgement from "./componentsReCheckJudgment/RecheckJudgment";
import BillAfterCourt from "../afterCourt/modal/BillAfterCourtOldData";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";
import AddEmsAndExport from "../afterCourt/modal/AddEmsAndExport";
import PayInSlipModal from "../afterCourt/report/QRcode";

const { Text } = Typography;

// ── Constants ──────────────────────────────────────────────────────────────────
const STATUS_GROUP = {
  11: "A",
  15: "B",
  16: "C",
  21: "D",
  97: "E",
  98: "E",
  99: "E",
};

const GROUP_API_STATUS = {
  A: 11,
  B: 15,
  C: 16,
  D: 21,
  E: 98,
};

const FILTER_RULES = {
  11: (r) => r.currentStatus === 11 || r.currentStatus === 10,
  15: (r) => r.currentStatus === 15,
  16: (r) => r.currentStatus === 16,
  21: (r) => r.currentStatus === 21,
  97: (r) => !r.roundDate && r.checkJudge && r.flagLoanNew === 1,
  98: (r) => r.roundDate != null && r.flagLoanNew === 1,
  99: (r) => r.checkJudge && r.flagLoanNew === 2,
};

// ── Main Component ─────────────────────────────────────────────────────────────
const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  // ── States ───────────────────────────────────────────────────────────────────
  const [groupCache, setGroupCache] = useState({});
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState(11);
  const [dataRecord, setDataRecord] = useState(null);
  const [isModal, setIsModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showEditJudgment, setShowEditJudgment] = useState(false);
  const [isModalAddEms, setIsModalAddEms] = useState(false);
  const [loanDataDB2, setLoanDataDB2] = useState([]);
  const [dateRound, setDateRound] = useState(dayjs());
  const [dataEms, setDataEms] = useState([]);
  const [showDate, setShowDate] = useState(false);
  const [payInItems, setPayInItems] = useState([]);
  const [isModalPayIn, setIsModalPayIn] = useState(false);

  const prevStatusRef = useRef(11); // ติดตาม status ก่อนหน้า

  // ── Status Options ────────────────────────────────────────────────────────────
  const statusOptions = useMemo(
    () => [
      { value: 11, icon: "📤", label: "รอรับงาน" },
      { value: 15, icon: "🕒", label: "รอตรวจสอบ" },
      { value: 16, icon: "✅", label: "ตรวจสอบแล้ว" },
      { value: 21, icon: "🔄", label: "โดนตีกลับ" },
      { value: 97, icon: "🕒", label: "รอส่ง EMS(ทำยอม)" },
      { value: 98, icon: "📮", label: "ส่ง EMS แล้ว(ทำยอม)" },
      { value: 99, icon: "🗂️", label: "พิพากษา" },
    ],
    [],
  );

  // ── Fetch API ─────────────────────────────────────────────────────────────────
  const fetchByGroup = useCallback(
    async (group, forceRefresh = false) => {
      if (!forceRefresh && groupCache[group]?.length > 0) {
        setDataArr(groupCache[group]);
        return;
      }
      console.log();

      setLoading(true);
      try {
        const apiStatus = GROUP_API_STATUS[group];
        const res = await axios.post(
          POST_MAIN_RECHECK_JUDGE_DOC,
          { userId, currentStatus: apiStatus },
          { headers: HEADERS_EXPORT_BEN },
        );
        const result = res.data?.length > 0 ? res.data : [];
        console.log("result", result);

        setGroupCache((prev) => ({ ...prev, [group]: result }));
        setDataArr(result);
        const preData = result.filter((item) => item.roundDate);
        setDataEms(preData);
        if (!result.length) message.info("ไม่มีข้อมูล");
      } catch (error) {
        if (error.name !== "CanceledError") {
          message.error(
            error.response?.data?.message ||
              error.message ||
              "ไม่สามารถโหลดข้อมูลได้",
          );
          setDataArr([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [userId, groupCache],
  );

  const loadLoanData = async (contno) => {
    setLoading(true);
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;

    try {
      const res = await axios.post(
        POST_LOAN_DB2,
        { CONTNO: `'${useContno}'` },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res?.data?.[0]) setLoanDataDB2(res.data?.[0]);
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadLoanData:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoading(false);
    }
  };

  // Force reload — ล้าง cache ของ group ปัจจุบัน แล้วดึงใหม่
  const reloadWithCacheClear = useCallback(async () => {
    setShowEditJudgment(false);
    setDataRecord(null);
    const group = STATUS_GROUP[processStatus];
    setGroupCache((prev) => ({ ...prev, [group]: [] }));
    await fetchByGroup(group, true);
  }, [processStatus, fetchByGroup]);

  // ── Change Status ─────────────────────────────────────────────────────────────
  const handleChangeStatus = useCallback(
    (value) => {
      if (value === processStatus || loading) return;
      console.log("va", value);

      const prevGroup = STATUS_GROUP[prevStatusRef.current];
      const newGroup = STATUS_GROUP[value];
      const isSameGroup = prevGroup === newGroup;

      clearSelectedRows();
      setSearchInputTable("");
      setProcessStatus(value);
      prevStatusRef.current = value;

      if (isSameGroup) {
        // ✅ กลุ่มเดียวกัน → ไม่ยิง API, ใช้ dataArr เดิม + filter ใน arrayTable
        return;
      }

      // ✅ ต่างกลุ่ม → ดึง API (มี cache ใช้ cache)
      fetchByGroup(newGroup);
    },
    [processStatus, loading, fetchByGroup],
  );

  // ── Auto-load ครั้งแรก ────────────────────────────────────────────────────────
  useEffect(() => {
    if (ROLE_ID === "2" || ROLE_ID === "1") {
      fetchByGroup(STATUS_GROUP[processStatus]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter + Sort (arrayTable) ────────────────────────────────────────────────
  const arrayTable = useMemo(() => {
    if (!dataArr || dataArr.length === 0) return [];

    const normalize = (v) =>
      String(v ?? "")
        .trim()
        .toLowerCase();
    const searchQuery = normalize(searchInputTable);
    const rule = FILTER_RULES[processStatus] ?? (() => true);

    const filtered = dataArr.filter((item) => {
      // 1. filter ตาม rule ของ status
      if (!rule(item)) return false;

      // 2. text search
      return (
        !searchQuery ||
        [
          item.parcelNo,
          item.newContno,
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM ?? ""}${item.NAME1 ?? ""} ${item.NAME2 ?? ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery))
      );
    });

    // sort: status 98 เรียงตาม roundDate ล่าสุดก่อน
    if (processStatus === 98) {
      return [...filtered].sort(
        (a, b) => dayjs(b.roundDate).valueOf() - dayjs(a.roundDate).valueOf(),
      );
    }

    return filtered;
  }, [dataArr, searchInputTable, processStatus]);

  const handleDataQuery = async () => {
    if (!selectedRows?.length) {
      message.warning("กรุณาเลือกรายการก่อน");
      return;
    }

    const payload = {
      id: selectedRows.map((row) => row.idJudge),
      roundDate: dateRound,
    };
    console.log("selectedRows", selectedRows);

    console.log("payload", payload);

    await insertData(payload);
  };

  const insertData = async (payload, opts = {}) => {
    const { silence = false } = opts;
    setLoading(true);

    try {
      const res = await axios.post(POST_ROUND_AGREEMENT, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const data = res.data;

      if (!data || (Array.isArray(data) && data.length === 0)) {
        if (!silence) message.warning("ไม่พบข้อมูล");
        return Array.isArray(data) ? data : [];
      }

      if (!silence) message.success("ดำเนินการสำเร็จ");
      fetchByGroup(STATUS_GROUP[processStatus], true);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      console.error("insertData error:", err);
      if (!silence) {
        message.error(`เกิดข้อผิดพลาด${status ? ` (${status})` : ""}`);
      }
      return null;
    } finally {
      setLoading(false);
      clearSelectedRows();
    }
  };

  // ── พิมพ์ใบเสร็จ (ใบนำฝาก) ของรายการที่เลือก ─────────────────────────────────
  const handlePrintPayIn = async () => {
    if (!selectedRows?.length) {
      message.warning("กรุณาเลือกรายการก่อน");
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all(
        selectedRows.map(async (row) => {
          const [detail, db2, bill] = await Promise.all([
            axios.post(
              POST_LOAD_DETAIL_OLD_DATA,
              {
                CONTNO: row.newContno,
                loanId: row.loanId,
                lawsuitId: row.lawsuitId,
              },
              { headers: HEADERS_EXPORT_BEN },
            ),
            axios.post(
              POST_LOAN_DB2,
              { CONTNO: `'${row.CONTNO}'` },
              { headers: HEADERS_EXPORT_BEN },
            ),
            axios.post(
              POST_BILL_PAYMENT,
              { CONTNO: row.newContno || "V" + row.CONTNO },
              { headers: HEADERS_EXPORT_BEN },
            ),
          ]);
          return {
            dataDB2: db2?.data?.[0] || null,
            billPayment: bill?.data || null,
            lawsuitData: detail?.data?.lawsult || null,
          };
        }),
      );
      setPayInItems(results);
      setIsModalPayIn(true);
    } catch (error) {
      console.error("handlePrintPayIn error:", error);
      message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลใบเสร็จ");
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    message.info("ล้างตัวกรองสำเร็จ");
  }, []);
  const handleOpenJudgment = useCallback((record) => {
    loadLoanData(record.CONTNO);
    setDataRecord(record);
    setShowEditJudgment(true);
  }, []);
  const handleCloseJudgment = useCallback(() => {
    setShowEditJudgment(false);
    setDataRecord(null);
  }, []);
  const handleReloadJudgment = reloadWithCacheClear;

  // ── API Helpers ───────────────────────────────────────────────────────────────
  const postDocStatus = async (payload, apiUse) => {
    try {
      const res = await axios.post(apiUse, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res.status === 200) return res.data;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;
      if (status === 404) message.error("ไม่พบปลายทาง (404)");
      else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      else if (status === 500)
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      else
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      throw error;
    }
  };

  const handleBulkSave = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("กรุณาเลือกรายการที่ต้องการดำเนินการ");
      return;
    }
    setLoading(true);
    let successCount = 0,
      failCount = 0;
    try {
      const selectedRecords = arrayTable.filter((item) =>
        selectedRowKeys.includes(item.CONTNO),
      );
      for (const record of selectedRecords) {
        try {
          await postDocStatus(
            { loanId: record.loanId, lawsuitId: record.lawsuitId, userId },
            POST_ACCEPT_RECHECK_JUDGE_DOC,
          );
          successCount++;
        } catch {
          failCount++;
        }
      }
      if (successCount > 0) {
        message.success(
          `รับงานสำเร็จ ${successCount} รายการ${failCount > 0 ? `, ล้มเหลว ${failCount} รายการ` : ""}`,
        );
        handleReloadJudgment();
      } else {
        message.error("ไม่สามารถดำเนินการได้");
      }
    } catch {
      message.error("เกิดข้อผิดพลาดในการดำเนินการ");
    } finally {
      setLoading(false);
    }
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const clearSelectedRows = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  // ── Columns ───────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 80,
      render: (_, __, i) => i + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      width: 150,
    },
    {
      title: "เลขสัญญาใหม่",
      dataIndex: "newContno",
      key: "newContno",
      align: "center",
      width: 150,
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "center",
      render: (_, r) => (
        <span>{`${r.SNAM ?? ""}${r.NAME1 ?? ""} ${r.NAME2 ?? ""}`.trim()}</span>
      ),
    },
    {
      title: "เลขคดีแดง",
      dataIndex: "redCaseNumber",
      key: "redCaseNumber",
      align: "center",
    },
    {
      title: "ผลพิจารณา",
      key: "flagLoanNew",
      align: "center",
      render: (_, r) => (
        <Tag
          color={
            r.flagLoanNew === 1 ? "green" : r.flagLoanNew === 2 ? "blue" : "red"
          }
        >
          {r.flagLoanNew === 1
            ? "ทำยอม"
            : r.flagLoanNew === 2
              ? "พิพากษา"
              : "รอตรวจสอบ"}
        </Tag>
      ),
    },
    processStatus < 90 && {
      title: "วันที่ดำเนินการ",
      align: "center",
      render: (_, r) => {
        const cfg = {
          11: { field: r.dtEnforce, label: "วางคำบังคับ", color: "red" },
          15: {
            field: r.dtAcceptCheckJudge,
            label: "รับงานเมื่อ",
            color: "blue",
          },
          16: {
            field: r.dtCompleteCheckJudge,
            label: "ตรวจสำเร็จเมื่อ",
            color: "green",
          },
          21: { field: r.dtFailDocAcc, label: "ตีกลับเมื่อ", color: "purple" },
        }[r.currentStatus];
        if (!cfg?.field) return <Tag color="default">-</Tag>;
        return (
          <Tag color={cfg.color}>
            {cfg.label}
            <br />
            {dayjs(cfg.field).add(543, "year").format("D MMM YY")}
          </Tag>
        );
      },
    },
    (processStatus === 97 || processStatus === 98) && {
      title: "รอบส่ง EMS",
      key: "roundDate",
      align: "center",
      render: (_, r) =>
        r.roundDate
          ? dayjs(r.roundDate).add(543, "year").format("DD MMM YY")
          : "-",
    },
    (processStatus === 97 || processStatus === 98) && {
      title: "เลขไปษณีย์",
      key: "parcelNo",
      align: "center",
      render: (_, r) => (
        <Tag>
          <Text strong copyable={!!r.parcelNo}>
            {r.parcelNo ?? "-"}
          </Text>
        </Tag>
      ),
    },
    processStatus !== 11 && {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => {
        if (processStatus === 15 || processStatus === 21) {
          return (
            <Button
              type="primary"
              ghost
              icon={<FileAddOutlined />}
              onClick={() => handleOpenJudgment(record)}
            >
              ตรวจสอบข้อมูล
            </Button>
          );
        }
        if (processStatus === 16 || processStatus > 90) {
          return (
            <Space>
              <Button
                color="green"
                variant="outlined"
                icon={<FileSearchOutlined />}
                onClick={() => {
                  setDataRecord(record);
                  setIsModal(true);
                }}
              >
                ดูข้อมูล
              </Button>

              <Button
                variant="outlined"
                color="orange"
                ghost
                disabled={
                  record.currentStatus === 11 ||
                  record.flagPay === 1 ||
                  processStatus > 90
                }
                onClick={() => handleOpenJudgment(record)}
              >
                ✏️ แก้ไขข้อมูล
              </Button>
            </Space>
          );
        }
      },
    },
  ].filter(Boolean);

  // ── Render ────────────────────────────────────────────────────────────────────
  if (showEditJudgment && dataRecord) {
    return (
      <ReCheckJudgement
        dataDefault={dataRecord}
        onClose={handleCloseJudgment}
        funcUpdateStatus={handleReloadJudgment}
        dataDB2={loanDataDB2}
      />
    );
  }

  return (
    <>
      {/* ── Status Chip Group ── */}
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 12,
          border: "1px solid #ffcb05",
          background: "#fffdf0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {/* Label */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              background: "#fff3cc",
              border: "1px solid #ffcb05",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 14, color: "#efa420" }}>🔍</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#efa420" }}>
              เลือกสถานะ
            </span>
          </div>

          {/* Chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {statusOptions.map(({ value, icon, label }) => {
              const isActive = processStatus === value;
              const isSameGroup =
                STATUS_GROUP[value] === STATUS_GROUP[processStatus];
              const cacheData = groupCache[STATUS_GROUP[value]] ?? [];

              // นับจำนวนของแต่ละ chip จาก cache + FILTER_RULES
              const chipCount = isActive
                ? arrayTable.length
                : isSameGroup && cacheData.length > 0
                  ? cacheData.filter(FILTER_RULES[value] ?? (() => true)).length
                  : null;

              return (
                <button
                  key={value}
                  onClick={() => handleChangeStatus(value)}
                  disabled={loading}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "6px 14px",
                    borderRadius: 20,
                    border: `1.5px solid ${
                      isActive ? "#efa420" : isSameGroup ? "#ffd966" : "#d9d9d9"
                    }`,
                    background: isActive
                      ? "linear-gradient(135deg, #ffcb05, #efa420)"
                      : isSameGroup
                        ? "#fffbe6"
                        : "#fff",
                    color: isActive ? "#fff" : "#555",
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 13,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.6 : 1,
                    boxShadow: isActive
                      ? "0 2px 8px rgba(239,164,32,0.45)"
                      : "none",
                    transition: "all 0.2s",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>{icon}</span>
                  <span>{label}</span>
                  {chipCount !== null && (
                    <span
                      style={{
                        marginLeft: 4,
                        background: isActive
                          ? "rgba(255,255,255,0.35)"
                          : "#fff3cc",
                        borderRadius: 10,
                        padding: "0 6px",
                        fontSize: 11,
                        fontWeight: 700,
                        color: isActive ? "#fff" : "#efa420",
                      }}
                    >
                      {chipCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {loading && (
            <span
              style={{
                marginLeft: "auto",
                color: "#efa420",
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
            >
              ⏳ กำลังโหลด...
            </span>
          )}
        </div>
      </Card>

      {/* ── Table Card ── */}
      <Card>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row gutter={[0, 16]}>
            <Col span={12} style={{ textAlign: "start" }}>
              {processStatus === 11 && (
                <Button
                  type="primary"
                  ghost
                  danger
                  icon={<InboxOutlined />}
                  onClick={handleBulkSave}
                  disabled={selectedRowKeys.length === 0}
                >
                  📥 รับงาน ({selectedRowKeys.length})
                </Button>
              )}

              {(processStatus === 97 || processStatus === 98) && (
                <Space size={10}>
                  <Switch
                    checked={showDate}
                    onChange={setShowDate}
                    checkedChildren="เลือกวันที่"
                    unCheckedChildren="เลือกวันที่"
                  />
                  <Space size={5} direction="vertical">
                    <Button
                      color="blue"
                      variant="filled"
                      icon={<PlusOutlined />}
                      size="large"
                      onClick={() => handleDataQuery()}
                      disabled={
                        selectedRows.length === 0 ||
                        selectedRows.some(
                          (item) => item.roundDate || item.flagLoanNew === 2,
                        )
                      }
                      loading={loading}
                      style={{ borderRadius: 8 }}
                    >
                      บันทึกรอบส่ง
                      {selectedRowKeys.length > 0 &&
                        ` (${selectedRowKeys.length})`}
                    </Button>

                    {showDate && (
                      <ThaiDatePicker
                        value={dateRound}
                        onChange={setDateRound}
                        placeholder="กรุณาเลือกวันที่"
                        size="large"
                        style={{ width: "100%" }}
                        suffixIcon={<CalendarOutlined />}
                      />
                    )}
                  </Space>
                  <Button
                    type="primary"
                    danger
                    icon={<BarcodeOutlined />}
                    onClick={() => setIsModalAddEms(true)}
                    disabled={loading}
                    loading={loading}
                  >
                    นำเข้าเลขไปรษณีย์
                  </Button>
                  <Button
                    color="purple"
                    variant="outlined"
                    icon={<FileSearchOutlined />}
                    onClick={handlePrintPayIn}
                    disabled={selectedRows.length === 0 || loading}
                  >
                    พิมพ์ใบเสร็จ
                    {selectedRowKeys.length > 0 &&
                      ` (${selectedRowKeys.length})`}
                  </Button>
                </Space>
              )}
            </Col>
            <Col span={12} style={{ textAlign: "end" }}>
              <Space size={10} wrap>
                <Input
                  placeholder="ค้นหาเลขสัญญา, ชื่อ"
                  value={searchInputTable}
                  onChange={(e) => setSearchInputTable(e.target.value)}
                  style={{ width: 250, borderRadius: 8 }}
                  size="small"
                  allowClear
                  suffix={<span style={{ marginRight: 5 }}>🔍</span>}
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilter}
                  size="large"
                  style={{
                    borderRadius: 8,
                    backgroundColor: "#fff1f0",
                    borderColor: "#ffa39e",
                    color: "#cf1322",
                  }}
                >
                  ล้างตัวกรอง
                </Button>
              </Space>
            </Col>

            <Col span={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={arrayTable}
                rowKey={(r) => r.CONTNO || r.id}
                scroll={{ x: 1000 }}
                rowSelection={
                  processStatus === 11 ||
                  processStatus === 97 ||
                  processStatus === 98
                    ? rowSelection
                    : null
                }
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                footer={() => (
                  <p style={{ margin: 0 }}>
                    จำนวนสัญญาทั้งหมด {arrayTable?.length}
                    {processStatus === 11 &&
                      ` | เลือก ${selectedRowKeys.length} รายการ`}
                  </p>
                )}
              />
            </Col>
          </Row>
        </Spin>

        {isModal && (
          <BillAfterCourt
            open={isModal}
            close={() => setIsModal(false)}
            dataDefault={dataRecord}
          />
        )}
        {isModalAddEms && (
          <AddEmsAndExport
            open={isModalAddEms}
            close={() => setIsModalAddEms(false)}
            dataDefault={dataEms}
            onRefresh={() => fetchByGroup(STATUS_GROUP[processStatus], true)}
          />
        )}
        {isModalPayIn && (
          <PayInSlipModal
            open={isModalPayIn}
            onClose={() => setIsModalPayIn(false)}
            items={payInItems}
          />
        )}
      </Card>
    </>
  );
};

const MainReCheckJudgement = MotionHoc(Main);
export default MainReCheckJudgement;
